import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { platformStaffAPI } from '../../../services';
import { UsersRound, Plus, Pencil, Trash2, Power, Search } from 'lucide-react';
import { PageHeader, PrimaryButton } from './theme';

const StaffManagementPage = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await platformStaffAPI.getStaff(search ? { search } : {});
      setStaff(Array.isArray(res?.data) ? res.data : []);
    } catch {
      showToast('Failed to load staff', 'error');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleToggleStatus = async (id) => {
    try {
      await platformStaffAPI.toggleStaffStatus(id);
      showToast('Status updated');
      load();
    } catch (err) {
      showToast(err?.message || 'Failed to update status', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await platformStaffAPI.deleteStaffMember(id);
      showToast('Staff member deleted');
      setDeleteId(null);
      load();
    } catch (err) {
      showToast(err?.message || 'Failed to delete staff member', 'error');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {toast.msg}
        </div>
      )}

      <PageHeader
        title="Staff Management"
        subtitle="DigiHR-internal staff accounts and the module permissions granted to each"
        actions={
          <PrimaryButton icon={Plus} onClick={() => navigate('/dashboard/staff/add')}>
            Add staff
          </PrimaryButton>
        }
      />

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, username, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : staff.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <UsersRound className="w-12 h-12 mx-auto mb-3 opacity-30" />
            No staff accounts found
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {staff.map((s) => (
                <tr key={s._id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{s.name}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{s.username}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{s.email}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {s.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <button onClick={() => navigate(`/dashboard/staff/${s._id}/edit`)} className="text-blue-600 hover:text-blue-800" title="Edit">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleToggleStatus(s._id)} className="text-gray-500 hover:text-gray-700" title={s.active ? 'Deactivate' : 'Activate'}>
                        <Power className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDeleteId(s._id)} className="text-red-600 hover:text-red-800" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm text-center">
            <p className="text-gray-900 font-semibold mb-2">Delete this staff account?</p>
            <p className="text-gray-500 text-sm mb-5">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagementPage;
